import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatEpochToESTDate } from './LaunchCalendar.utility';

import type { ProductDetailState } from 'reducers/detail/productDetail';
import SwankySwatchBox from 'components/productdetail/swankyswatch/SwankySwatchBox';
import { Price } from 'components/productdetail/Price';
import { evLaunchCalendarSubscriptionClick, LAUNCH_CALENDAR_FLOW_TYPE } from 'events/launchCalendar';
import { track } from 'apis/amethyst';
import { fetchProductDetails, subscribeToUpcomingStylesNotification } from 'actions/launchCalendar';
import { INTERNET_EMAIL } from 'common/regex';
import { SmallLoader } from 'components/Loader';
import Carousel from 'components/common/Carousel/Carousel';
import MelodyModal from 'components/common/MelodyModal';
import ProductName from 'components/productdetail/ProductName';
import ProductDescription from 'components/productdetail/description/ProductDescription';
import { LAUNCH_CALENDAR_NOTIFICATION_SUBSCRIBED_COPY } from 'constants/launchCalendar';
import ProductUtils from 'helpers/ProductUtils';
import { constructMSAImageUrl, noop } from 'helpers/index';
import useEffectOnce from 'hooks/useEffectOnce';
import { selectLaunchCalendar } from 'selectors/launchCalendar';
import type { AppDispatch } from 'entrypoints/bootstrapOnClient';

import css from 'styles/components/launchcalendar/upcomingStyleQuickViewModal.scss';

interface Props {
  closeModal: () => void;
  productId: string;
  styleId: string;
  showNotifyMe: boolean;
  relatedStyleIds: number[];
}

export const UpcomingStyleQuickViewModal = ({ styleId, productId: defaultProductId, showNotifyMe, closeModal, relatedStyleIds }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const { upcomingStylesQuickViewData } = useSelector(selectLaunchCalendar);
  const [currentStyleId, setCurrentStyleId] = useState(styleId);

  const [isSubscribedForNotifcation, setIsSubscribedForNotifcation] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const errorMessageSetter = (message: string) => {
    setErrorMessage(message);
  };

  useEffectOnce(() => {
    const fetchOpts = {
      productId: defaultProductId,
      includeOos: true,
      includeStocksWithoutAsins: true
    };

    dispatch(fetchProductDetails(fetchOpts, errorMessageSetter));
  });

  const submitSubscriptionForUpcomingStyle = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const {
      currentTarget: { emailAddress }
    } = e;
    dispatch(subscribeToUpcomingStylesNotification(emailAddress.value, Number(currentStyleId)));
    setIsSubscribedForNotifcation(true);
    track(() => [
      evLaunchCalendarSubscriptionClick,
      {
        flowType: LAUNCH_CALENDAR_FLOW_TYPE.UpcomingStyleModal,
        isItemSetupCompleted: true,
        styleId: Number(currentStyleId)
      }
    ]);
  };

  const onColorChange = (styleId: string) => {
    setIsSubscribedForNotifcation(false);
    setCurrentStyleId(styleId);
  };

  const buildNotifyMeSection = () => {
    const emailRegex = INTERNET_EMAIL.toString().slice(2, -2);

    if (isSubscribedForNotifcation) {
      return <div className={css.notificationSubscribed}>{LAUNCH_CALENDAR_NOTIFICATION_SUBSCRIBED_COPY}</div>;
    }
    return (
      <form method="post" onSubmit={submitSubscriptionForUpcomingStyle}>
        <div className={css.notifyMeContainer}>
          <fieldset className={css.fieldSet}>
            <label className={css.fieldSetLabel} htmlFor="productNotifyEmailAddress">
              Email
            </label>
            <input
              type="email"
              id="upcomingStyleNotifyEmailAddress"
              className={css.emailAddress}
              name="emailAddress"
              pattern={emailRegex}
              required
            />
          </fieldset>
        </div>
        <div className={css.actionBar}>
          <button type="submit" className={css.sendButton}>
            Notify Me
          </button>
        </div>
      </form>
    );
  };

  const buildErrorMessageContent = () => <div className={css.errorMessage}>{errorMessage}</div>;

  const buildModalContent = () => {
    if (errorMessage) {
      return buildErrorMessageContent();
    }

    const productData = upcomingStylesQuickViewData[defaultProductId];
    if (!productData) {
      return <SmallLoader />;
    }
    const { detail } = productData;
    const { defaultProductType, description, productId, styles } = detail;
    const productStyles = ProductUtils.getStyleMap(styles);

    const style = productStyles[currentStyleId];
    if (!style) {
      return buildErrorMessageContent();
    }
    const { images, colorId, hardLaunchDate, originalPrice, price } = style;

    return (
      <div className={css.container}>
        <div className={css.productGallery}>
          {images.length > 1 ? (
            <Carousel
              slides={images.map(image => {
                const { imageId, type } = image;
                return <img className={css.galleryImage} key={type} src={constructMSAImageUrl(imageId, { autoCrop: true })} alt={type} />;
              })}
              slideWidths={{ mobile: '100%', tablet: '100%', desktop: '100%' }}
            />
          ) : (
            <>
              {images.length > 0 && (
                <>
                  <img className={css.galleryImage} src={constructMSAImageUrl(images[0]!.imageId)} alt={images[0]!.type} />
                </>
              )}
            </>
          )}
        </div>
        <div className={css.productInfo}>
          <ProductName showSocialLinks={false} colorId={colorId} product={detail} />
          <div className={css.releaseDate}>
            Release Date
            <span className={css.releaseDateValue}>{formatEpochToESTDate({ epoch: Number(hardLaunchDate) })}</span>
          </div>
          {(originalPrice || price) && <Price productStyle={style} percentOffText="OFF" showPercentOffBanner={false} />}
          <SwankySwatchBox
            isGiftCard={false}
            onColorChange={onColorChange}
            productState={{ detail } as ProductDetailState}
            productType={defaultProductType}
            selectedStyle={productStyles[currentStyleId]!}
            styleList={ProductUtils.filterStylesByRelatedStyleIdsInOrder(styles, relatedStyleIds)}
            isHydraColorLegend={false}
            pageType={'USQVM'}
            shouldCheckStock={false}
          />
          {showNotifyMe && buildNotifyMeSection()}
          {description && (
            <>
              <h3 className={css.productInfoHeading}>Product Information</h3>
              <ProductDescription
                descriptionItems={description}
                productId={productId}
                defaultProductType={defaultProductType}
                isExpanded={true}
                allowCollapse={false}
                onReportError={noop}
                showGlossary={false}
                showReportError={false}
                brandLogo={<></>}
                glossaryLink=""
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <MelodyModal
      className={css.modal}
      isOpen={true}
      onRequestClose={closeModal}
      contentLabel="Upcoming Style Quick View"
      wrapperTestId="upcomingStyleQuickView"
    >
      {buildModalContent()}
    </MelodyModal>
  );
};

export default UpcomingStyleQuickViewModal;
